<template>
    <b-modal
             id="edit-athlete-modal"
             :title="title"
             v-model="showModal"
             centered no-close-on-backdrop
             @ok="handleOk"
             @cancel="handleCancel"
             @close="handleCancel">

        <b-overlay :show="isLoading" rounded="sm">

            <ErrorView :visible="isLoadingError" :errorValue="loadingErrorValue"/>

            <form v-if="isLoadingError == false" ref="form" v-on:submit.prevent="noop">
                <b-form-group
                        label="Name"
                        label-for="name-input"
                        invalid-feedback="Name is required (minimum 3 characters, max 128)"
                        :state="nameState">
                    <b-form-input
                            id="name-input"
                            placeholder="Enter name"
                            v-model="nameValue"
                            required>
                    </b-form-input>
                </b-form-group>

                <b-form-group
                        label="Birth Date"
                        label-for="birthdate-input"
                        invalid-feedback="Birth Date is required"
                        :state="birthDateState">
                    <b-form-datepicker
                            id="birthdate-input"
                            v-model="birthDateValue"
                            value-as-date>
                    </b-form-datepicker>
                </b-form-group>

                <div class="container-fluid m-0 p-0">
                    <div class="row">
                        <div class="col">
                            <b-form-group
                                    label="Height"
                                    label-for="height-input"
                                    invalid-feedback="Height is required (between 1 and 500)"
                                    :state="heightState">
                                <b-form-input
                                        id="height-input"
                                        type="number"
                                        v-model="heightValue"
                                        required>
                                </b-form-input>
                            </b-form-group>
                        </div>
                        <div class="col justify-content-end">
                            <b-form-group class="hide-form-group-label"
                                    label="Units"
                                    label-for="heightunit-input"
                                    invalid-feedback="Required"
                                    :state="heightUnitState">
                                <b-form-select id="heightunit-input" :options="heightUnitOptions" v-model="heightUnitValue" required>
                                    <template v-slot:first>
                                        <b-form-select-option :value="null">Please select an option</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <b-form-group
                                    label="Weight"
                                    label-for="weight-input"
                                    invalid-feedback="Weight is required (between 1 and 500)"
                                    :state="weightState">
                                <b-form-input
                                        id="weight-input"
                                        type="number"
                                        v-model="weightValue"
                                        required>
                                </b-form-input>
                            </b-form-group>
                        </div>
                        <div class="col hide-form-group-label">
                            <b-form-group
                                    label="Units"
                                    label-for="weightunit-input"
                                    invalid-feedback="Required"
                                    :state="weightUnitState">
                                <b-form-select id="weightunit-input" :options="weightUnitOptions" v-model="weightUnitValue" required>
                                    <template v-slot:first>
                                        <b-form-select-option :value="null">Please select an option</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </div>
                    </div>
                </div>

                <b-form-group
                        label="Sex"
                        label-for="sex-input"
                        invalid-feedback="Sex is required"
                        description="For algorithms in the test reports."
                        :state="sexState">
                    <b-form-select id="sex-input" :options="sexOptions" v-model="sexValue" required>
                        <template v-slot:first>
                            <b-form-select-option :value="null">Please select an option</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>
            </form>
        </b-overlay>

        <template v-slot:modal-footer="{ ok, cancel }">
            <b-button size="sm" variant="primary" @click="ok()">
                Save Changes
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="cancel()">
                Cancel
            </b-button>
        </template>

        <b-modal id="error-modal" size="sm" title="Action failed" ok-only centered>
            <p class="m-2">There was a problem completing the action, Please try again.</p>
        </b-modal>

    </b-modal>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import ErrorView from '../ErrorView'

    export default {
        name: "AthleteModal",
        components: {
            ErrorView
        },
        data() {
            return {
                showModal: false,
                showErrorAlert: true,

                title: "Athlete Details",

                nameValue: null,
                weightValue: null,
                weightUnitValue: null,
                heightValue: null,
                heightUnitValue: null,
                sexValue: null,
                birthDateValue: null,

                showValidation: false,

                sexOptions: [
                    { value: 'male', text: 'Male' },
                    { value: 'female', text: 'Female' },
                ],
                heightUnitOptions: [
                    { value: 'centimeter', text: 'Centimeter' },
                    { value: 'inch', text: 'Inch' },
                ],
                weightUnitOptions: [
                    { value: 'kilogram', text: 'Kilogram' },
                    { value: 'pound', text: 'Pound' },
                ]
            }
        },
        watch: {
            model(newValue) {
                this.resetModel(newValue);
            },
            showModal(newValue) {
                if(newValue) this.resetModel(this.model);
            }
        },
        computed: {
            ...mapGetters({
                model: 'athlete/current',
                isModelLoading: 'athlete/currentLoading',
                isModelLoadingError: 'athlete/currentLoadingError',
                modelLoadingErrorValue: 'athlete/currentLoadingErrorValue',
            }),

            isLoading: function() {
                return this.isModelLoading;
            },
            isLoadingError: function() {
                return this.isModelLoadingError;
            },
            loadingErrorValue: function() {
                return this.modelLoadingErrorValue;
            },

            nameState: function() {
                if(this.nameValue && this.nameValue.length > 2 && this.nameValue.length < 128) {
                    return true
                }
                return this.showValidation ? false : null
            },
            weightState: function() {
                if(this.weightValue && parseFloat(this.weightValue) > 0 && parseFloat(this.weightValue) < 500) {
                    return true
                }
                return this.showValidation ? false : null
            },
            weightUnitState: function() {
                if(this.weightUnitValue) {
                    return true;
                }
                return this.showValidation ? false : null
            },
            heightState: function() {
                if(this.heightValue && parseFloat(this.heightValue) > 0 && parseFloat(this.heightValue) < 500) {
                    return true
                }
                return this.showValidation ? false : null
            },
            heightUnitState: function() {
                if(this.heightUnitValue) {
                    return true;
                }
                return this.showValidation ? false : null
            },
            sexState: function() {
                if(this.sexValue) {
                    return true;
                }
                return this.showValidation ? false : null
            },
            birthDateState: function() {
                if(this.birthDateValue) {
                    return true;
                }
                return this.showValidation ? false : null
            },

            validatedValue: function() {

                if(this.isLoading || this.isLoadingError) {
                    return null;
                }

                if(this.nameState &&
                   this.weightState &&
                   this.weightUnitState &&
                   this.heightState &&
                   this.heightUnitState &&
                   this.birthDateState &&
                   this.sexState) {
                    let value = {
                        organizationId: this.model ? this.model.organizationId : undefined,
                        athleteId: this.model ? this.model.athleteId : undefined,
                        name: this.nameValue,
                        weight: parseFloat(this.weightValue),
                        height: parseFloat(this.heightValue),
                        weightUnit: this.weightUnitValue,
                        heightUnit: this.heightUnitValue,
                        sex: this.sexValue,
                        birthDateObject: this.birthDateValue
                    };

                    return value;
                }

                return null;
            },
        },
        methods: {
            ...mapActions({
                save: 'athlete/save',
            }),
            resetModel(newValue) {
                this.showValidation = false;
                if(newValue) {
                    this.title = "Edit Athlete Details"
                    this.nameValue = newValue.name;
                    this.weightValue = newValue.weight;
                    this.heightValue = newValue.height;
                    this.weightUnitValue = newValue.weightUnit;
                    this.heightUnitValue = newValue.heightUnit;
                    this.sexValue = newValue.sex;
                    this.birthDateValue = newValue.birthDateObject;
                } else {
                    this.title = "New Athlete Details"
                    this.nameValue = null;
                    this.weightValue = null;
                    this.heightValue = null;
                    this.weightUnitValue = null;
                    this.heightUnitValue = null;
                    this.sexValue = null;
                    this.birthDateValue = null;
                }

                if(!this.weightUnitValue) {
                    this.weightUnitValue = 'kilogram';
                }
                if(!this.heightUnitValue) {
                    this.heightUnitValue = 'centimeter';
                }

            },
            handleOk(bvModalEvt) {

                // Prevent modal from closing
                bvModalEvt.preventDefault()

                if(this.isLoading) {
                    return
                }

                if(this.validatedValue != null) {

                    let self = this;
                    this.save(this.validatedValue)
                        .then(function (success) {
                            if(success) {
                                self.resetModel(null);
                                self.closeModal();
                            } else {
                                self.$bvModal.show('error-modal')
                            }
                        })

                } else {
                    this.showValidation = true;
                }
            },
            handleCancel(bvModalEvt) {
                // Prevent modal from closing
                bvModalEvt.preventDefault()

                if(this.isLoading) {
                    return
                }

               this.closeModal()

            },
            closeModal() {
                this.$nextTick(() => {
                    this.$bvModal.hide('edit-athlete-modal')
                })
            },
            noop () {
                // do nothing ?
            }
        }
    }
</script>

<style scoped>

    div.hide-form-group-label {
        color: white;
    }

</style>